import {
  AccountingConnectionsResponse,
  IQuickBook,
  INetsuite,
  Rillet,
  RilletCoaListResponse,
  RilletFormType,
  Xero,
  XeroCoaListResponse,
  XeroCoaUpdateRequest,
  XeroUserInfoResponse,
  XeroCompaniesListResponse,
  XeroSetCompanyRequest,
} from '@monetize/types/app';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useToast } from '../services/toast';
import { apiGet, apiPatch, apiPost, apiPut } from './axios';
import { settingsQueryKeys } from './settingsService';

export const connectorServiceQueryKeys = {
  base: ['connector'] as const,
  xero: () => [...connectorServiceQueryKeys.base, 'xero'] as const,
  xeroUserInfo: () =>
    [...connectorServiceQueryKeys.xero(), 'userinfo'] as const,
  xeroCoa: () => [...connectorServiceQueryKeys.xero(), 'coa'] as const,
  xeroConnection: () =>
    [...connectorServiceQueryKeys.xero(), 'connection'] as const,
  xeroCompanies: () =>
    [...connectorServiceQueryKeys.xero(), 'companies'] as const,
  rillet: () => [...connectorServiceQueryKeys.base, 'rillet'] as const,
  rilletCoa: () => [...connectorServiceQueryKeys.rillet(), 'coa'] as const,
};

export function useSaveNetsuiteConfiguration(
  options: Partial<UseMutationOptions<INetsuite, unknown, INetsuite>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: (data) =>
      apiPost<INetsuite>(
        `connector/auth/accounting/netsuite/authorize`,
        data,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      const cache = queryClient.getQueryData<AccountingConnectionsResponse>(
        settingsQueryKeys.accounting(),
      );
      if (cache) {
        queryClient.setQueryData(settingsQueryKeys.accounting(), {
          ...cache,
          netsuite: data,
        });
      }
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.accounting(),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useGetXeroUserInfo<SelectData = XeroUserInfoResponse>(
  options: Partial<
    UseQueryOptions<XeroUserInfoResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...connectorServiceQueryKeys.xeroUserInfo()],
    queryFn: () =>
      apiGet<XeroUserInfoResponse>('connector/accounting/xero/userinfo').then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useGetXeroCoa<SelectData = XeroCoaListResponse>(
  options: Partial<
    UseQueryOptions<XeroCoaListResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...connectorServiceQueryKeys.xeroCoa()],
    queryFn: () =>
      apiGet<XeroCoaListResponse>('connector/accounting/xero/coa').then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useGetXeroCompanies<SelectData = XeroCompaniesListResponse>(
  options: Partial<
    UseQueryOptions<XeroCompaniesListResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...connectorServiceQueryKeys.xeroCompanies()],
    queryFn: () =>
      apiGet<XeroCompaniesListResponse>(
        'connector/accounting/xero/companies',
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useUpdateXeroConnection(
  options: Partial<
    UseMutationOptions<void, unknown, XeroCoaUpdateRequest>
  > = {},
) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: XeroCoaUpdateRequest) => {
      await apiPatch<void>('connector/accounting/xero/connection', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.accounting()],
      });

      addToast({
        summary: 'Success',
        detail: 'Xero connection settings updated successfully',
        severity: 'success',
      });
    },
    ...options,
  });
}

export function useSetXeroCompany(
  options: Partial<
    UseMutationOptions<void, unknown, XeroSetCompanyRequest>
  > = {},
) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: XeroSetCompanyRequest) => {
      await apiPost<void>('connector/accounting/xero/companies', payload);
    },
    onSuccess: () => {
      // Invalidate COA (company-specific)
      queryClient.invalidateQueries({
        queryKey: [...connectorServiceQueryKeys.xeroCoa()],
      });

      // Invalidate connections to refresh company display
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.accounting()],
      });

      addToast({
        summary: 'Success',
        detail: 'Xero company selected successfully',
        severity: 'success',
      });
    },
    ...options,
  });
}

export function useGetRilletCoa<SelectData = RilletCoaListResponse>(
  options: Partial<
    UseQueryOptions<RilletCoaListResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...connectorServiceQueryKeys.rilletCoa()],
    queryFn: () =>
      apiGet<RilletCoaListResponse>('connector/accounting/rillet/coa').then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useSaveRilletConfiguration(
  options: Partial<UseMutationOptions<Rillet, unknown, RilletFormType>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: (data) =>
      apiPost<Rillet>(`connector/auth/accounting/rillet/authorize`, data).then(
        (res) => res.data,
      ),
    onSuccess: (data, variables, context) => {
      const cache = queryClient.getQueryData<AccountingConnectionsResponse>(
        settingsQueryKeys.accounting(),
      );
      if (cache) {
        queryClient.setQueryData(settingsQueryKeys.accounting(), {
          ...cache,
          rillet: data,
        });
      }
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.accounting(),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

const PROVIDER_DISPLAY_NAME: Record<
  keyof AccountingConnectionsResponse,
  string
> = {
  quickbooks: 'QuickBooks',
  netsuite: 'NetSuite',
  xero: 'Xero',
  rillet: 'Rillet',
};

export interface UpdateAccountingConnectionPayload {
  type: keyof AccountingConnectionsResponse;
  action: 'enable' | 'disable';
}

export function useUpdateAccountingConnectionStatus(
  options: Partial<
    UseMutationOptions<
      IQuickBook | INetsuite | Xero | Rillet,
      unknown,
      UpdateAccountingConnectionPayload
    >
  > = {},
) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ type, action }: UpdateAccountingConnectionPayload) =>
      apiPut<IQuickBook | INetsuite | Xero | Rillet>(
        `connector/accounting/${type}/${action}`,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.accounting(),
      });

      addToast({
        summary: 'Success',
        detail: `${PROVIDER_DISPLAY_NAME[variables.type]} connection ${variables.action}d successfully`,
        severity: 'success',
      });

      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
